import { Flex } from '@chakra-ui/react';
import ModalAddonDetailed from '@view/components/Modals/AddonDetailed';
import { useModal } from '@molecules/BEOffScreen';
import BEImage from '@molecules/BEImage';
import BEText from '@molecules/Text';
import BEWeekdays from '@molecules/BEWeekdays';
import BEStepper from '@molecules/Stepper';
import BEButton from '@molecules/Button/Button';
import {
  StyledMainBodyContainer,
  StyledTitle,
  StyledPrice,
  StyledCardContainer,
} from './StyledAddonCard';
import Addon from '@data/models/Addon';
import { useMemo, useState } from 'react';
import { useStore } from '@data/other/withStore';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import useFormat from '@business/utils/hooks/useFormat';

export interface AddonCardProps {
  item: Addon;
  onClickAdd?: (item: Addon, amount: number) => void;
  onClickRemove?: (item: Addon) => void;
}

const AddonCard = ({ item, onClickAdd, onClickRemove }: AddonCardProps) => {
  const {
    property: { cart },
  } = useStore();
  const { images, name, rate, chargeType, weekAvailability = [] } = item;
  const addonDetailedModal = useModal(ModalAddonDetailed, {});
  const { currency } = useFormat();

  const { t } = useTranslation('common');

  const [amount, setAmount] = useState<number>(1);

  const onCart = useMemo(() => {
    return (
      cart.items.findIndex(
        (x) => x.model?.type === 'addon' && x.getItemId() === item.id
      ) >= 0
    );
  }, [cart.items, item.id]);

  const handleViewDetailClick = () => {
    addonDetailedModal.open({ ...item });
  };

  return (
    <StyledCardContainer>
      <BEImage
        src={images.length > 0 ? images[0].src : ''}
        alt={images.length > 0 ? images[0].alt : ''}
        fit="cover"
        height={168}
        onClick={handleViewDetailClick}
      />
      <StyledMainBodyContainer>
        <StyledTitle type="h3">{name.getText()}</StyledTitle>
        <StyledPrice>
          <BEText type="h3">{currency(rate)}</BEText>
          <BEText type="card-body" color="neutral" darkness="700">
            {t(`addon-${chargeType}`).toLowerCase()}
          </BEText>
        </StyledPrice>
        {[
          'per_night',
          'per_accommodation_per_night',
          'per_guest_per_night',
        ].includes(chargeType) && (
          <BEWeekdays gap={4} weekdays={weekAvailability} />
        )}
      </StyledMainBodyContainer>
      <Flex paddingX={4} paddingBottom={4} justifyContent={'space-between'}>
        <BEButton
          color="primary"
          variant="link"
          onClick={handleViewDetailClick}
        >
          {t('view-details')}
        </BEButton>

        <Flex gap={4}>
          {chargeType === 'quantity' && (
            <BEStepper
              size="md"
              value={amount}
              minValue={1}
              maxValue={
                item.maxQtyPerReservation === 0
                  ? undefined
                  : item.maxQtyPerReservation
              }
              onChange={setAmount}
            />
          )}
          {!onCart ? (
            <BEButton
              color="primary"
              variant="outline"
              onClick={() => onClickAdd?.(item, amount)}
            >
              {t('add')}
            </BEButton>
          ) : (
            <BEButton
              color="primary"
              variant="outline"
              onClick={() => onClickRemove?.(item)}
              icon="trash-can"
            >
              {t('remove')}
            </BEButton>
          )}
        </Flex>
      </Flex>
    </StyledCardContainer>
  );
};

export default observer(AddonCard);
